import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Pressable, TextInput, ActivityIndicator, useWindowDimensions } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../Admin/constants/adminColors';
import FontFamily from '../../constants/fonts';
import {
  QUEUE_BANDS,
  WORKLOAD_TIMEFRAMES,
} from './peopleHubMockData';
import {
  QueueItem,
  QueueBandKey,
  QueueScope,
  QueueActivityType,
  WorkloadView,
  WorkloadTimeframe,
  OnboardingStepStatus,
} from './types';
import {
  fetchQueue,
  fetchActivities,
  fetchOnboardingRamp,
  fetchLocationWorkload,
  OnboardingRampResponse,
  RampHire,
  LocationWorkloadResponse,
  WorkloadTimeframeParam,
} from './peopleHubApi';
import { formatUserName } from '../../utils/emailTouserName';
import {
  setActivityHistoryLoading,
  setActivityHistoryData,
  setCoverageThunk,
  reassignTaskThunk,
  acceptCoverageAction,
  endCoverageAction,
} from '../../store/slices/peopleHubSlice';
import { useToast } from '../../components/LhToastContainer';
import { SetCoverageModal, LeaderOption } from './SetCoverageModal';
import { ReassignTaskModal, ReassignTaskSubmitData } from './ReassignTaskModal';

// Default All History / Recent Activity query — kept in one place so Home and the
// All History tab agree on what counts as a "default view" cache hit.
export const DEFAULT_ACTIVITY_HISTORY_SIZE = 25;
export const RECENT_ACTIVITY_VISIBLE_COUNT = 10;

// Maps a /people/activities ActivityDTO row to the Recent People Activity row shape.
interface RecentActivityRow {
  id: string;
  employeeName: string;
  type: QueueActivityType;
  statusLabel: string;
  isCompleted: boolean;
  dateLabel: string;
}

const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  MISSED: 'Missed',
};

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const mapActivityToRecentRow = (activity: Record<string, any>): RecentActivityRow => ({
  id: String(activity.id),
  employeeName: activity.employeeName ?? '—',
  type: TASK_TYPE_LABELS[activity.type] ?? 'Training',
  statusLabel: ACTIVITY_STATUS_LABELS[activity.status] ?? activity.status ?? '—',
  isCompleted: activity.status === 'COMPLETED',
  dateLabel: formatShortDate(activity.createdOn),
});

const TASK_TYPE_LABELS: Record<string, QueueActivityType> = {
  PULSE_CHECK: 'Pulse Check',
  OBSERVATION: 'Observation',
  TRAINING: 'Training',
  CERTIFICATION: 'Certification',
  MILESTONE: 'Milestone',
};

// Maps a /people/queue TaskDTO row (see PeoplePortController#getQueue) to the row shape this screen renders.
const mapTaskToQueueItem = (task: Record<string, any>): QueueItem | null => {
  if (task.status === 'COMPLETED') return null;

  const type = TASK_TYPE_LABELS[task.taskType] ?? 'Training';
  const isInProgress = task.status === 'IN_PROGRESS';

  // Declared outside the band logic because it is also returned as `daysUntilDue`.
  const diffDays: number | undefined =
    typeof task.daysUntilDue === 'number'
      ? task.daysUntilDue
      : task.dueDate
        ? Math.round((new Date(task.dueDate).getTime() - Date.now()) / 86400000)
        : undefined;

  let band: QueueBandKey;
  let dueLabel: string;
  if (isInProgress) {
    band = 'progress';
    dueLabel = 'Saved draft';
  } else {
    const days = diffDays ?? 0;
    if (task.status === 'MISSED' || days < 0) {
      band = 'overdue';
      const lateDays = Math.abs(days);
      dueLabel = `${lateDays} day${lateDays === 1 ? '' : 's'} late`;
    } else {
      band = 'upcoming';
      dueLabel = days === 0 ? 'Due today' : `In ${days} day${days === 1 ? '' : 's'}`;
    }
  }

  const assignedToName = task.assigneeEmail ? formatUserName(task.assigneeEmail) : (task.assignee ?? '—');
  const offeredToName = task.offeredToEmail ? formatUserName(task.offeredToEmail) : task.offeredTo;
  const originalAssigneeName = task.originalAssigneeEmail ? formatUserName(task.originalAssigneeEmail) : task.originalAssignee;

  return {
    id: String(task.id),
    employeeName: task.employeeName ?? '—',
    employeeRole: task.role ?? '—',
    type,
    assignedTo: assignedToName,
    assigneeEmail: task.assigneeEmail ?? task.assignee,
    canReassign: (type === 'Observation' || type === 'Pulse Check') && !isInProgress,
    band,
    dueLabel,
    employeeId: task.employeeId != null ? String(task.employeeId) : undefined,
    observationType: task.observationType ?? null,
    activityId: task.activityId ?? null,
    dueDate: task.dueDate ? String(task.dueDate).slice(0, 10) : undefined,
    daysUntilDue: diffDays,
    reassignCount: typeof task.reassignCount === 'number' ? task.reassignCount : 0,
    originalAssignee: task.originalAssignee,
    originalAssigneeName: originalAssigneeName,
    offeredTo: task.offeredTo,
    offeredToName: offeredToName,
    reassignReason: task.reassignReason,
  };
};

const TYPE_BADGE_COLORS: Record<QueueActivityType, { bg: string; color: string }> = {
  'Pulse Check': { bg: AdminColors.blue900, color: AdminColors.textOnDark },
  'Observation': { bg: AdminColors.blue700, color: AdminColors.textOnDark },
  'Training': { bg: AdminColors.tagPurple.text, color: AdminColors.textOnDark },
  'Certification': { bg: AdminColors.green600, color: AdminColors.textOnDark },
  'Milestone': { bg: AdminColors.tagOrange.text, color: AdminColors.textOnDark },
};

const WORKLOAD_DOT_COLORS: Record<QueueActivityType, string> = {
  'Pulse Check': AdminColors.tagTeal.text,
  'Observation': AdminColors.blue900,
  'Training': AdminColors.tagPurple.text,
  'Certification': AdminColors.green600,
  'Milestone': AdminColors.tagOrange.text,
};

// /people/location-workload `timeframe` query values for the Home timeframe toggle.
const TIMEFRAME_PARAMS: Record<WorkloadTimeframe, WorkloadTimeframeParam> = {
  cw: 'CW',
  period: 'PERIOD',
  quarter: 'QUARTER',
  all: 'ALL_OPEN',
};

const WORKLOAD_CATEGORY_TYPES: Record<string, QueueActivityType> = {
  PULSE_CHECKS: 'Pulse Check',
  OBSERVATIONS: 'Observation',
  TRAINING: 'Training',
  CERTIFICATIONS: 'Certification',
  MILESTONES: 'Milestone',
};

// By-leader stacked bar: category segments reuse the category dot colors; STILL_OPEN is neutral.
const getLeaderSegmentColor = (key: string): string => {
  const type = WORKLOAD_CATEGORY_TYPES[key];
  return type ? WORKLOAD_DOT_COLORS[type] : AdminColors.gray200;
};

// /people/onboarding/ramp checkpoint `state` -> step strip status.
const RAMP_STATE_TO_STATUS: Record<string, OnboardingStepStatus> = {
  done: 'complete',
  late: 'behind',
  current: 'current',
  ahead: 'pending',
};

const getRampBadgeColors = (hire: RampHire): { bg: string; text: string } => {
  const status = (hire.status ?? '').toUpperCase();
  if (status === 'ON_TRACK' && !hire.lateCount) return { bg: AdminColors.green75, text: AdminColors.green600 };
  if (status.includes('LATE') || status.includes('BEHIND') || status.includes('OVERDUE')) {
    return { bg: AdminColors.tagRed.bg, text: AdminColors.tagRed.text };
  }
  return { bg: AdminColors.tagOrange.bg, text: AdminColors.tagOrange.text };
};

const ONBOARDING_STEP_COLORS: Record<OnboardingStepStatus, { bg: string; dot: string; border?: string }> = {
  complete: { bg: AdminColors.green75, dot: AdminColors.green600 },
  behind: { bg: AdminColors.red75, dot: AdminColors.red400 },
  current: { bg: AdminColors.blue50, dot: AdminColors.blue700, border: AdminColors.blue700 },
  pending: { bg: AdminColors.gray50, dot: AdminColors.gray200 },
};

type SortKey = 'employee' | 'due';

interface FilterMenuProps {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const FilterMenu: React.FC<FilterMenuProps> = ({ label, options, selected, onChange, isOpen, onToggle }) => {
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(
    () => options.filter((option) => option.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );

  const toggleValue = (value: string) => {
    const next = new Set(selected);
    next.has(value) ? next.delete(value) : next.add(value);
    onChange(next);
  };

  return (
    <View>
      <TouchableOpacity onPress={onToggle} accessibilityLabel={`Filter by ${label}`} hitSlop={6}>
        <MaterialIcons
          name="unfold-more"
          size={16}
          color={selected.size > 0 ? AdminColors.blue700 : AdminColors.gray350}
        />
      </TouchableOpacity>
      {isOpen && (
        <>
          <TouchableOpacity style={styles.filterBackdrop} activeOpacity={1} onPress={onToggle} />
          <View style={styles.filterMenu}>
            <View style={styles.filterSearchBox}>
              <MaterialIcons name="search" size={14} color={AdminColors.gray350} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search..."
                placeholderTextColor={AdminColors.gray350}
                style={styles.filterSearchInput}
              />
            </View>
            <View style={styles.filterActionsRow}>
              <TouchableOpacity onPress={() => onChange(new Set(options))}>
                <Text style={styles.filterActionText}>Select All</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => onChange(new Set())}>
                <Text style={styles.filterActionText}>Clear</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.filterOptionList} nestedScrollEnabled>
              {filteredOptions.map((option) => (
                <TouchableOpacity key={option} style={styles.filterOption} onPress={() => toggleValue(option)}>
                  <MaterialIcons
                    name={selected.has(option) ? 'check-box' : 'check-box-outline-blank'}
                    size={16}
                    color={selected.has(option) ? AdminColors.blue700 : AdminColors.gray350}
                  />
                  <Text style={styles.filterOptionText} numberOfLines={1}>{option}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={styles.filterFooterText}>{selected.size} of {options.length} selected</Text>
          </View>
        </>
      )}
    </View>
  );
};

interface SortHeaderProps {
  label: string;
  active: boolean;
  dir: 1 | -1;
  onPress: () => void;
}

const SortHeader: React.FC<SortHeaderProps> = ({ label, active, dir, onPress }) => (
  <TouchableOpacity style={styles.sortHeader} onPress={onPress}>
    <Text style={styles.headCellText}>{label}</Text>
    <MaterialIcons
      name={dir === 1 ? 'arrow-upward' : 'arrow-downward'}
      size={14}
      color={active ? AdminColors.blue700 : AdminColors.gray350}
    />
  </TouchableOpacity>
);

interface PeopleHubHomeProps {
  onOpenHistory?: () => void;
  /** Opens the Employee Profile screen for this employee. */
  onOpenEmployee?: (employeeName: string, employeeId?: string, employeeRole?: string) => void;
  /** Resume this queue row's Pulse Check / Observation activity, or open Begin Activity when the task has no activity yet. */
  onOpenQueueActivity?: (item: QueueItem) => void | Promise<void>;
}

const PeopleHubHome: React.FC<PeopleHubHomeProps> = ({ onOpenHistory, onOpenEmployee, onOpenQueueActivity }) => {
  const isDesktop = useWindowDimensions().width >= 1024;
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [resumingRowId, setResumingRowId] = useState<string | null>(null);

  // ── My Queue state ──────────────────────────────────────────────────────────
  const [scope, setScope] = useState<QueueScope>('my');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [assignedFilter, setAssignedFilter] = useState<Set<string>>(new Set());
  const [topicFilter, setTopicFilter] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'due', dir: 1 });
  const [collapsedBands, setCollapsedBands] = useState<Set<QueueBandKey>>(new Set());
  const [openFilterColumn, setOpenFilterColumn] = useState<'type' | 'assigned' | 'topic' | null>(null);

  // ── Location Workload state ──────────────────────────────────────────────────
  const [workloadView, setWorkloadView] = useState<WorkloadView>('site');
  const [timeframe, setTimeframe] = useState<WorkloadTimeframe>('cw');
  const [showAllLeaders, setShowAllLeaders] = useState(false);

  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const locationName: string | undefined = selectedLocation?.name ?? selectedLocation?.displayText;
  const userEmail: string | undefined = useSelector((state: any) => state.appData?.userInfo?.email);
  const userGpid: string | undefined = useSelector((state: any) => state.appData?.userInfo?.GPID);
  const fieldUsers = useSelector((state: any) => state.appData?.FieldUsers ?? state.appData?.fieldUsers ?? state.appData?.fieldUser ?? []);
  const coeUsers = useSelector((state: any) => state.appData?.COEUsers ?? state.appData?.coeUsers ?? []);
  const employees = useSelector((state: any) => state.appData?.employees);
  const hasActiveEmployees = useMemo(() => {
    const list: any[] = Array.isArray(employees) ? employees : employees?.data ?? [];
    return list.some((e: any) => e.Active_vs_Inactive === 'Active' || e.Active_vs_Inactive === undefined);
  }, [employees]);

  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [isQueueLoading, setIsQueueLoading] = useState(false);
  const [isCoverageModalOpen, setIsCoverageModalOpen] = useState(false);
  const [isCoverageSubmitting, setIsCoverageSubmitting] = useState(false);
  const [reassignModalItem, setReassignModalItem] = useState<QueueItem | null>(null);
  const [isReassignSubmitting, setIsReassignSubmitting] = useState(false);
  const userInfo = useSelector((state: any) => state.appData?.userInfo);
  const userRoleId = userInfo?.roleId;
  const userRoleName = userInfo?.roleName;
  const dispatch = useDispatch();
  const toast = useToast();
  const coverage = useSelector((state: any) => state.peopleHub?.home?.coverage);
  const activityHistoryCache = useSelector((state: any) => state.peopleHub?.activityHistory);
  const isRecentActivityLoading = activityHistoryCache?.loadingState === 'loading';
  const recentActivity = useMemo<RecentActivityRow[]>(
    () => (activityHistoryCache?.rows ?? []).slice(0, RECENT_ACTIVITY_VISIBLE_COUNT).map(mapActivityToRecentRow),
    [activityHistoryCache?.rows],
  );

  const loadRecentActivity = useCallback(async () => {
    if (!locationName) return;
    dispatch(setActivityHistoryLoading());
    const query = {
      locationName,
      search: '',
      sortBy: 'createdOn',
      sortOrder: 'DESC' as const,
      page: 1,
      size: DEFAULT_ACTIVITY_HISTORY_SIZE,
    };
    try {
      const res = await fetchActivities(query);
      dispatch(setActivityHistoryData({
        query,
        rows: res?.activities ?? [],
        total: res?.pagination?.total ?? (res?.activities ?? []).length,
      }));
    } catch (err) {
      console.warn('Failed to load recent People Hub activity', err);
      dispatch(setActivityHistoryData({ query, rows: [], total: 0 }));
    }
  }, [locationName, dispatch]);

  useEffect(() => {
    // Always refetch the latest queue + activities whenever the Home tab loads.
    loadRecentActivity();
  }, [loadRecentActivity]);

  const loadQueue = useCallback(async () => {
    if (!locationName) return;
    setIsQueueLoading(true);
    try {
      const res = await fetchQueue({
        locationName,
        locationId: selectedLocation?.id,
        sapId: selectedLocation?.sapId,
        scope: scope === 'my' ? 'MY_TASKS' : 'ALL_LOCATION_TASKS',
        ...(scope === 'my' ? { userEmail } : {}),
        size: 100,
      });
      const tasks: Record<string, any>[] = res?.tasks ?? [];
      setQueueItems(tasks.map(mapTaskToQueueItem).filter((item): item is QueueItem => item !== null));
    } catch (err) {
      console.warn('Failed to load People Hub queue', err);
      setQueueItems([]);
    } finally {
      setIsQueueLoading(false);
    }
  }, [locationName, selectedLocation?.id, selectedLocation?.sapId, scope, userEmail]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // ── New Hire Onboarding ramp ────────────────────────────────────────────────
  const [ramp, setRamp] = useState<OnboardingRampResponse | null>(null);
  const [isRampLoading, setIsRampLoading] = useState(false);
  const rampHires = ramp?.hires ?? [];

  useEffect(() => {
    if (!locationName) return;
    let cancelled = false;
    setIsRampLoading(true);
    fetchOnboardingRamp(locationName)
      .then((res) => { if (!cancelled) setRamp(res); })
      .catch((err) => {
        console.warn('Failed to load People Hub onboarding ramp', err);
        if (!cancelled) setRamp(null);
      })
      .finally(() => { if (!cancelled) setIsRampLoading(false); });
    return () => { cancelled = true; };
  }, [locationName]);

  // ── Location Workload ───────────────────────────────────────────────────────
  const [workload, setWorkload] = useState<LocationWorkloadResponse | null>(null);
  const [isWorkloadLoading, setIsWorkloadLoading] = useState(false);

  useEffect(() => {
    if (!locationName) return;
    let cancelled = false;
    setIsWorkloadLoading(true);
    fetchLocationWorkload(locationName, TIMEFRAME_PARAMS[timeframe])
      .then((res) => { if (!cancelled) setWorkload(res); })
      .catch((err) => {
        console.warn('Failed to load People Hub location workload', err);
        if (!cancelled) setWorkload(null);
      })
      .finally(() => { if (!cancelled) setIsWorkloadLoading(false); });
    return () => { cancelled = true; };
  }, [locationName, timeframe]);

  const selectedHire = useMemo(
    () => (selectedEmployeeId ? rampHires.find((h) => h.gpid === selectedEmployeeId) ?? null : null),
    [rampHires, selectedEmployeeId],
  );

  const scopedItems = queueItems;

  const typeOptions = useMemo(() => Array.from(new Set(scopedItems.map((i) => i.type))).sort(), [scopedItems]);
  const assignedOptions = useMemo(
    () => Array.from(new Set(scopedItems.map((i) => i.assignedTo))).sort(),
    [scopedItems],
  );
  const topicOptions = useMemo(
    () => Array.from(new Set(scopedItems.flatMap((i) => (i.tags ?? []).map((t) => t.label)))).sort(),
    [scopedItems],
  );

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopedItems.filter((item) => {
      if (
        selectedHire &&
        item.employeeId !== selectedHire.gpid &&
        item.employeeName !== selectedHire.fullName
      ) {
        return false;
      }
      if (typeFilter.size && !typeFilter.has(item.type)) return false;
      if (assignedFilter.size && !assignedFilter.has(item.assignedTo)) return false;
      if (topicFilter.size && !(item.tags ?? []).some((t) => topicFilter.has(t.label))) return false;
      if (!q) return true;
      const topics = (item.tags ?? []).map((t) => t.label).join(' ');
      return `${item.employeeName} ${item.employeeRole} ${item.type} ${item.assignedTo} ${topics}`
        .toLowerCase()
        .includes(q);
    });
  }, [scopedItems, search, typeFilter, assignedFilter, topicFilter, selectedHire]);

  const sortRows = (rows: QueueItem[]): QueueItem[] => {
    const sorted = [...rows].sort((a, b) => {
      const cmp =
        sort.key === 'employee'
          ? a.employeeName.localeCompare(b.employeeName)
          : (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31');
      return sort.dir * cmp;
    });
    return sorted;
  };

  const bandedItems = useMemo(
    () =>
      QUEUE_BANDS.map((band) => ({
        band,
        rows: sortRows(filteredItems.filter((item) => item.band === band.key)),
      })).filter((group) => group.rows.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredItems, sort],
  );

  const toggleSort = (key: SortKey) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  };

  const toggleBand = (key: QueueBandKey) => {
    setCollapsedBands((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleEmployeeSelection = (employeeId: string) => {
    setSelectedEmployeeId((prev) => (prev === employeeId ? null : employeeId));
  };

  // Observation / Pulse Check rows resume the activity screen (or open Begin Activity when the task has no
  // activityId yet); everything else opens the employee's My Team profile.
  const isActivityRow = (item: QueueItem) => item.type === 'Observation' || item.type === 'Pulse Check';

  const handleEmployeeNamePress = (item: QueueItem) => {
    onOpenEmployee?.(item.employeeName, item.employeeId, item.employeeRole);
  };

  const handleRowPress = async (item: QueueItem) => {
    if (isActivityRow(item)) {
      setResumingRowId(item.id);
      try {
        await onOpenQueueActivity?.(item);
      } finally {
        setResumingRowId(null);
      }
    } else {
      onOpenEmployee?.(item.employeeName, item.employeeId, item.employeeRole);
    }
  };

  const leaders = workload?.leaders ?? [];
  const leaderVisibleCount = workload?.leaderVisibleCount ?? leaders.length;
  const visibleLeaders = showAllLeaders ? leaders : leaders.slice(0, leaderVisibleCount);

  const leaderOptions: LeaderOption[] = useMemo(() => {
    const allUsers = [...fieldUsers, ...coeUsers];
    const map = new Map<string, LeaderOption>();
    const employeeList: any[] = Array.isArray(employees) ? employees : employees?.data ?? [];
    const empShiftMap = new Map<string, string>();
    employeeList.forEach((e: any) => {
      const email = (e.Email || e.email || '').toLowerCase().trim();
      const shift = e.Shift || e.shift;
      if (email && shift) empShiftMap.set(email, shift);
    });

    const loggedInEmail = (userEmail ?? '').toLowerCase().trim();

    allUsers.forEach((u: any) => {
      const email = (u?.UserEmail ?? u?.email ?? u?.userEmail ?? '').trim();
      if (!email || !email.includes('@')) return;
      const lower = email.toLowerCase();
      if (loggedInEmail && lower === loggedInEmail) return; // exclude current user
      if (!map.has(lower)) {
        let name = '';
        if (u.FirstName && u.LastName) {
          name = `${u.FirstName} ${u.LastName}`.trim();
        } else if (u.First_Name && u.Last_Name) {
          name = `${u.First_Name} ${u.Last_Name}`.trim();
        } else {
          name = formatUserName(email);
        }
        const shift = u.Shift || u.shift || empShiftMap.get(lower) || undefined;
        map.set(lower, {
          id: email,
          name,
          gpid: u?.GPID != null ? String(u.GPID) : undefined,
          shift,
          displayText: shift ? `${name} (${shift} shift)` : name,
        });
      }
    });

    const sortedList = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));

    if (sortedList.length === 0) {
      return [
        { id: 'marcus.webb@pepsico.com', name: 'Marcus Webb', shift: '2nd', displayText: 'Marcus Webb (2nd shift)' },
        { id: 'rita.alvarez@pepsico.com', name: 'Rita Alvarez', shift: '3rd', displayText: 'Rita Alvarez (3rd shift)' },
        { id: 'jordan.blake@pepsico.com', name: 'Jordan Blake', shift: '1st', displayText: 'Jordan Blake (1st shift)' },
        { id: 'renee.cho@pepsico.com', name: 'Renee Cho', shift: '2nd', displayText: 'Renee Cho (2nd shift)' },
      ];
    }
    return sortedList;
  }, [fieldUsers, coeUsers, employees, userEmail]);

  const handleCoverageSubmit = async (data: {
    coverFrom: string;
    coverTo: string;
    coveredBy: string;
    coveredByName: string;
    taskIds: string[];
  }) => {
    setIsCoverageSubmitting(true);
    try {
      const coveringLeader = leaderOptions.find((l) => l.id === data.coveredBy);
      await (dispatch(
        setCoverageThunk({
          ...data,
          locationName: locationName ?? '',
          originalLeaderGpid: userGpid != null ? String(userGpid) : '',
          coveredByGpid: coveringLeader?.gpid ?? '',
        }) as any,
      ) as any).unwrap();
      setIsCoverageModalOpen(false);
      toast?.showToast?.({
        header: 'Coverage Set',
        subtext: `Covered by ${data.coveredByName} from ${formatShortDate(data.coverFrom)} to ${formatShortDate(data.coverTo)}.`,
        variant: 'success',
      });
      loadQueue();
    } catch (err) {
      console.warn('Failed to set coverage', err);
      toast?.showToast?.({
        header: 'Error',
        subtext: typeof err === 'string' ? err : 'Failed to set coverage. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsCoverageSubmitting(false);
    }
  };

  const handleOpenReassign = (item: QueueItem) => {
    setReassignModalItem(item);
  };

  const handleReassignSubmit = async (data: ReassignTaskSubmitData) => {
    setIsReassignSubmitting(true);
    try {
      const assignee = leaderOptions.find((l) => l.id === data.reassignTo);
      await (dispatch(
        reassignTaskThunk({
          ...data,
          actingUserGpid: userGpid != null ? String(userGpid) : '',
          reassignToGpid: assignee?.gpid ?? '',
        }) as any,
      ) as any).unwrap();
      const isOffer = !data.isDirectReassign;
      const activityType = reassignModalItem?.type ?? 'Task';
      const employeeName = reassignModalItem?.employeeName ?? '';
      setReassignModalItem(null);
      toast?.showToast?.({
        header: isOffer ? 'Task Offered' : 'Task Reassigned',
        subtext: isOffer
          ? `Offered ${activityType} for ${employeeName} to ${data.reassignToName}.`
          : `Reassigned ${activityType} for ${employeeName} to ${data.reassignToName}.`,
        variant: 'success',
      });
      loadQueue();
    } catch (err) {
      console.warn('Failed to reassign task', err);
      toast?.showToast?.({
        header: 'Error',
        subtext: typeof err === 'string' ? err : 'Failed to reassign task. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsReassignSubmitting(false);
    }
  };

  const handleEndCoverage = () => {
    dispatch(endCoverageAction());
    toast?.showToast?.({
      header: 'Coverage Ended',
      subtext: 'Coverage ended. Open tasks are back with you.',
      variant: 'success',
    });
    loadQueue();
  };

  const handleAcceptCoverage = () => {
    dispatch(acceptCoverageAction());
    toast?.showToast?.({
      header: 'Offers Accepted',
      subtext: 'Tasks accepted and moved to your queue.',
      variant: 'success',
    });
    loadQueue();
  };

  return (
    <ScrollView contentContainerStyle={[styles.container, isDesktop ? styles.containerRow : styles.containerColumn]}>
      {/* ── My Queue ──────────────────────────────────────────────────────── */}
      <View style={[styles.queueCard, isDesktop && styles.queueCardDesktop]}>
        <Text style={styles.cardTitle}>My Queue</Text>
        <Text style={styles.cardCopy}>
          Select a pulse check or observation row to begin it, with employee, role and activity type prepopulated
          before the full screen workflow opens. Certification, training and milestone rows open that employee's
          profile, where the onboarding work is recorded. Pulse checks are assigned every two weeks and observations
          every quarter, each due at the end of its window, so neither can run overdue. Anything not completed by
          the due date closes as Missed in All History and cannot be made up.
        </Text>

        <View style={styles.toolbar}>
          <View style={styles.scopeGroup}>
            <TouchableOpacity
              style={[styles.scopeButton, scope === 'my' && styles.scopeButtonActive]}
              onPress={() => setScope('my')}
            >
              <Text style={[styles.scopeLabel, scope === 'my' && styles.scopeLabelActive]}>My Tasks</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.scopeButton, scope === 'all' && styles.scopeButtonActive]}
              onPress={() => setScope('all')}
            >
              <Text style={[styles.scopeLabel, scope === 'all' && styles.scopeLabelActive]}>All Tasks</Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.searchBox, isSearchFocused && styles.searchBoxFocused]}>
            <MaterialIcons name="search" size={16} color={AdminColors.gray350} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search people, activities, assignees and topics"
              placeholderTextColor={AdminColors.gray350}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              style={styles.searchInput}
            />
          </View>
          <Text style={styles.countLabel}>{filteredItems.length} of {scopedItems.length}</Text>
          {isQueueLoading && <ActivityIndicator size="small" color={AdminColors.blue700} />}
          <TouchableOpacity
            style={styles.coverageButton}
            onPress={() => setIsCoverageModalOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Set coverage"
          >
            <MaterialIcons name="people" size={15} color={AdminColors.blue700} />
            <Text style={styles.coverageButtonText}>Set coverage</Text>
          </TouchableOpacity>
        </View>

        {coverage && coverage.active !== false && (
          <View style={styles.coverBanner}>
            <View style={styles.coverBannerTextWrap}>
              {coverage.isCovering ? (
                <Text style={styles.coverBannerText}>
                  Covering for <Text style={styles.coverBannerStrong}>{coverage.delegatorName || 'Leader'}</Text> until {formatShortDate(coverage.coverageTo)}.
                </Text>
              ) : (
                <Text style={styles.coverBannerText}>
                  <Text style={styles.coverBannerStrong}>
                    Covered by {coverage.coveredByName || coverage.coveredBy}
                  </Text>
                  {' from '}{formatShortDate(coverage.coverageFrom)}{' to '}{formatShortDate(coverage.coverageTo)}. {coverage.offeredCount} offered, {coverage.acceptedCount} accepted.
                </Text>
              )}
            </View>
            <View style={styles.coverBannerActions}>
              {!coverage.isCovering && coverage.offeredCount > 0 && (
                <TouchableOpacity
                  style={styles.coverOfferBtn}
                  onPress={handleAcceptCoverage}
                >
                  <Text style={styles.coverOfferBtnText}>Accept all</Text>
                </TouchableOpacity>
              )}
              {!coverage.isCovering && (
                <TouchableOpacity
                  style={styles.coverOfferBtn}
                  onPress={handleEndCoverage}
                >
                  <Text style={styles.coverOfferBtnText}>End coverage</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        <View style={[styles.tableHead, openFilterColumn && styles.tableHeadFilterOpen]}>
          <View style={styles.colEmployee}>
            <SortHeader
              label="Employee Name"
              active={sort.key === 'employee'}
              dir={sort.key === 'employee' ? sort.dir : 1}
              onPress={() => toggleSort('employee')}
            />
          </View>
          <View style={[styles.colType, openFilterColumn === 'type' && styles.colFilterOpen]}>
            <Text style={styles.headCellText}>Type</Text>
            <FilterMenu
              label="Type"
              options={typeOptions}
              selected={typeFilter}
              onChange={setTypeFilter}
              isOpen={openFilterColumn === 'type'}
              onToggle={() => setOpenFilterColumn((prev) => (prev === 'type' ? null : 'type'))}
            />
          </View>
          <View style={[styles.colAssigned, openFilterColumn === 'assigned' && styles.colFilterOpen]}>
            <Text style={styles.headCellText}>Assigned to</Text>
            <FilterMenu
              label="Assigned to"
              options={assignedOptions}
              selected={assignedFilter}
              onChange={setAssignedFilter}
              isOpen={openFilterColumn === 'assigned'}
              onToggle={() => setOpenFilterColumn((prev) => (prev === 'assigned' ? null : 'assigned'))}
            />
          </View>
          <View style={[styles.colTopic, openFilterColumn === 'topic' && styles.colFilterOpen]}>
            <Text style={styles.headCellText}>Topic</Text>
            <FilterMenu
              label="Topic"
              options={topicOptions}
              selected={topicFilter}
              onChange={setTopicFilter}
              isOpen={openFilterColumn === 'topic'}
              onToggle={() => setOpenFilterColumn((prev) => (prev === 'topic' ? null : 'topic'))}
            />
          </View>
          <View style={styles.colDue}>
            <SortHeader
              label="Due"
              active={sort.key === 'due'}
              dir={sort.key === 'due' ? sort.dir : 1}
              onPress={() => toggleSort('due')}
            />
          </View>
        </View>

        {bandedItems.length === 0 ? (
          <View style={styles.emptyState}>
            {isQueueLoading ? (
              <>
                <ActivityIndicator size="small" color={AdminColors.blue700} />
                <Text style={styles.emptyStateText}>Loading queue…</Text>
              </>
            ) : queueItems.length === 0 ? (
              <Text style={styles.emptyStateText}>
                {hasActiveEmployees
                  ? 'You have no open people activities. Pulse checks are assigned every two weeks and observations every period.'
                  : 'No active employees are assigned to this location.'}
              </Text>
            ) : (
              <Text style={styles.emptyStateText}>Nothing in this view. Clear the search or filters to see more.</Text>
            )}
          </View>
        ) : (
          bandedItems.map(({ band, rows }) => {
            const isCollapsed = collapsedBands.has(band.key);
            return (
              <View key={band.key}>
                <TouchableOpacity
                  style={[styles.bandHeader, styles[`bandHeader_${band.key}` as const]]}
                  onPress={() => toggleBand(band.key)}
                >
                  <MaterialIcons
                    name={isCollapsed ? 'chevron-right' : 'expand-more'}
                    size={18}
                    color={AdminColors.textSecondary}
                  />
                  <Text style={styles.bandTitle}>{band.title}</Text>
                  <Text style={styles.bandCount}>({rows.length})</Text>
                  <Text style={styles.bandCopy} numberOfLines={1}>{band.copy}</Text>
                </TouchableOpacity>
                {!isCollapsed &&
                  rows.map((item) => {
                    const badge = TYPE_BADGE_COLORS[item.type];
                    const isResumingRow = resumingRowId === item.id;
                    return (
                      <Pressable
                        key={item.id}
                        // @ts-ignore — onHoverIn/onHoverOut are valid on React Native Web
                        onHoverIn={() => setHoveredRowId(item.id)}
                        onHoverOut={() => setHoveredRowId(null)}
                        onPress={() => handleRowPress(item)}
                        disabled={isResumingRow}
                        style={[styles.row, hoveredRowId === item.id && styles.rowHovered]}
                      >
                        <View style={styles.colEmployee}>
                          <Pressable onPress={() => handleEmployeeNamePress(item)} hitSlop={4}>
                            <Text style={styles.employeeName}>{item.employeeName}</Text>
                          </Pressable>
                          <Text style={styles.employeeRole}>{item.employeeRole}</Text>
                        </View>
                        <View style={styles.colType}>
                          <View style={[styles.typeBadge, { backgroundColor: badge.bg }]}>
                            <Text style={[styles.typeBadgeText, { color: badge.color }]}>{item.type}</Text>
                          </View>
                        </View>
                        <View style={styles.colAssigned}>
                          <Text style={styles.assignedTo} numberOfLines={1}>{item.assignedTo}</Text>
                          {scope === 'all' && item.originalAssigneeName && item.originalAssigneeName !== item.assignedTo && (
                            <Text style={styles.reassignedFromText}>Reassigned from {item.originalAssigneeName}</Text>
                          )}
                          {item.offeredToName ? (
                            <View style={styles.offeredTag}>
                              <Text style={styles.offeredTagText}>Offered to {formatUserName(item.offeredToName)}</Text>
                            </View>
                          ) : item.canReassign ? (
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation?.();
                                handleOpenReassign(item);
                              }}
                              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                            >
                              <Text style={styles.reassignLink}>Reassign</Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <View style={styles.colTopic}>
                          {item.tags && item.tags.length > 0 ? (
                            <View style={styles.topicTagRow}>
                              {item.tags.map((tag) => (
                                <View
                                  key={tag.label}
                                  style={[
                                    styles.topicTag,
                                    {
                                      backgroundColor:
                                        tag.tone === 'phase'
                                          ? AdminColors.tagBlue.bg
                                          : tag.tone === 'metric'
                                            ? AdminColors.tagRed.bg
                                            : AdminColors.tagGray.bg,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.topicTagText,
                                      {
                                        color:
                                          tag.tone === 'phase'
                                            ? AdminColors.tagBlue.text
                                            : tag.tone === 'metric'
                                              ? AdminColors.tagRed.text
                                              : AdminColors.tagGray.text,
                                      },
                                    ]}
                                  >
                                    {tag.label}
                                  </Text>
                                </View>
                              ))}
                            </View>
                          ) : (
                            <Text style={styles.noOpenAction}>No open action</Text>
                          )}
                        </View>
                        <View style={styles.colDue}>
                          {isResumingRow ? (
                            <ActivityIndicator size="small" color={AdminColors.blue700} />
                          ) : (
                            <Text style={styles.dueLabel}>{item.dueLabel}</Text>
                          )}
                        </View>
                      </Pressable>
                    );
                  })}
              </View>
            );
          })
        )}
      </View>

      {/* ── Side column ───────────────────────────────────────────────────── */}
      <View style={[styles.sideColumn, isDesktop && styles.sideColumnDesktop]}>
        {/* New Hire Onboarding */}
        <View style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <Text style={styles.cardTitle}>New Hire Onboarding</Text>
            <View style={styles.badgePillBlue}>
              <Text style={styles.badgePillBlueText}>{ramp?.tag ?? `${rampHires.length} in ramp`}</Text>
            </View>
          </View>
          <Text style={styles.cardCopy}>
            Select a hire to narrow the queue to their obligations. The strip is their path: Week 1 to Milestone 3.
          </Text>
          {rampHires.length === 0 && (
            <View style={styles.recentLoadingRow}>
              {isRampLoading && <ActivityIndicator size="small" color={AdminColors.blue700} />}
              <Text style={styles.emptyStateText}>
                {isRampLoading ? 'Loading new hires…' : 'No new hires in ramp.'}
              </Text>
            </View>
          )}
          {rampHires.map((employee) => {
            const isSelected = selectedEmployeeId === employee.gpid;
            const badgeColors = getRampBadgeColors(employee);
            return (
            <View key={employee.gpid} style={[styles.onboardingCard, isSelected && styles.onboardingCardSelected]}>
              <View style={styles.onboardingTopRow}>
                <View style={styles.onboardingAvatar}>
                  <MaterialIcons name="person" size={16} color={AdminColors.blue700} />
                </View>
                <View style={styles.onboardingInfo}>
                  <TouchableOpacity onPress={() => toggleEmployeeSelection(employee.gpid)}>
                    <Text style={styles.onboardingName}>{employee.fullName}</Text>
                  </TouchableOpacity>
                  <Text style={styles.onboardingMeta}>{employee.subtitle ?? employee.role}</Text>
                </View>
                <View style={[styles.onboardingBadge, { backgroundColor: badgeColors.bg }]}>
                  <Text style={[styles.onboardingBadgeText, { color: badgeColors.text }]}>
                    {employee.statusLabel ?? employee.status}
                  </Text>
                </View>
              </View>
              <View style={styles.onboardingStripRow}>
                {employee.checkpoints.map((step) => {
                  const colors = ONBOARDING_STEP_COLORS[RAMP_STATE_TO_STATUS[step.state] ?? 'pending'];
                  return (
                    <View
                      key={step.shortLabel}
                      style={[
                        styles.onboardingStep,
                        { backgroundColor: colors.bg },
                        colors.border ? { borderColor: colors.border, borderWidth: 2 } : null,
                      ]}
                    >
                      <Text style={styles.onboardingStepLabel}>{step.shortLabel}</Text>
                      <View style={[styles.onboardingStepDot, { backgroundColor: colors.dot }]} />
                      <Text style={styles.onboardingStepCount}>{step.done}/{step.total}</Text>
                    </View>
                  );
                })}
              </View>
            </View>
            );
          })}
          {selectedHire && (
            <TouchableOpacity onPress={() => setSelectedEmployeeId(null)} style={styles.clearSelectionBanner}>
              <Text style={styles.clearSelectionText}>
                Showing {selectedHire.fullName}'s queue. Show everything again.
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Location Workload */}
        <View style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <Text style={styles.cardTitle}>Location Workload</Text>
            {isWorkloadLoading ? (
              <ActivityIndicator size="small" color={AdminColors.blue700} />
            ) : workload?.summary?.tag ? (
              <View style={styles.badgePillRed}>
                <Text style={styles.badgePillRedText}>{workload.summary.tag}</Text>
              </View>
            ) : null}
          </View>
          <View style={styles.toggleGroup}>
            <TouchableOpacity
              style={[styles.toggleButton, workloadView === 'site' && styles.toggleButtonActive]}
              onPress={() => setWorkloadView('site')}
            >
              <Text style={[styles.toggleLabel, workloadView === 'site' && styles.toggleLabelActive]}>
                Site overview
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toggleButton, workloadView === 'leader' && styles.toggleButtonActive]}
              onPress={() => setWorkloadView('leader')}
            >
              <Text style={[styles.toggleLabel, workloadView === 'leader' && styles.toggleLabelActive]}>
                By leader
              </Text>
            </TouchableOpacity>
          </View>
          <View style={styles.timeframeRow}>
            {WORKLOAD_TIMEFRAMES.map((tf) => (
              <TouchableOpacity
                key={tf.id}
                style={[styles.timeframeButton, timeframe === tf.id && styles.timeframeButtonActive]}
                onPress={() => setTimeframe(tf.id)}
              >
                <Text style={[styles.timeframeLabel, timeframe === tf.id && styles.timeframeLabelActive]}>
                  {tf.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {!workload ? (
            <View style={styles.recentLoadingRow}>
              <Text style={styles.emptyStateText}>
                {isWorkloadLoading ? 'Loading workload…' : 'Workload is unavailable right now.'}
              </Text>
            </View>
          ) : workloadView === 'site' ? (
            <>
              <Text style={styles.cardCopy}>{workload.summary?.leadText}</Text>
              {workload.categories.length === 0 && !!workload.emptyText && (
                <Text style={styles.emptyStateText}>{workload.emptyText}</Text>
              )}
              {workload.categories.map((category) => {
                const color = WORKLOAD_DOT_COLORS[WORKLOAD_CATEGORY_TYPES[category.type]] ?? AdminColors.gray200;
                return (
                  <View key={category.type} style={styles.workloadRow}>
                    <View style={styles.workloadLabelRow}>
                      <View style={styles.workloadLabelWithDot}>
                        <View style={[styles.workloadDot, { backgroundColor: color }]} />
                        <Text style={styles.workloadLabel}>{category.label}</Text>
                      </View>
                      <Text style={[styles.workloadMeta, category.overdue > 0 && styles.workloadMetaLate]}>
                        {category.detail ?? `${category.overdue} overdue, ${category.upcoming} upcoming`}
                      </Text>
                    </View>
                    <View style={styles.progressTrack}>
                      <View
                        style={[styles.progressFill, { width: `${category.pct}%`, backgroundColor: color }]}
                      />
                    </View>
                    <Text style={styles.workloadSub}>
                      {category.progressText ?? `${category.completed} of ${category.total} complete, ${category.pct}%`}
                    </Text>
                  </View>
                );
              })}
              {!!workload.siteNote && (
                <View style={styles.noteBox}>
                  <Text style={styles.noteText}>{workload.siteNote}</Text>
                </View>
              )}
            </>
          ) : (
            <>
              <Text style={styles.cardCopy}>{workload.leaderLeadText}</Text>
              {leaders.length === 0 && !!workload.emptyText && (
                <Text style={styles.emptyStateText}>{workload.emptyText}</Text>
              )}
              {visibleLeaders.map((leader, index) => (
                <View
                  key={leader.gpid ?? leader.email ?? `leader-${index}`}
                  style={[styles.workloadRow, leader.currentUser && styles.leaderRowCurrent]}
                >
                  <View style={styles.workloadLabelRow}>
                    <Text style={styles.workloadLabel} numberOfLines={1}>
                      {leader.displayName ?? leader.name ?? 'Unknown leader'}
                      {leader.shift ? ` · ${leader.shift}` : ''}
                    </Text>
                    {!!leader.scoreText && <Text style={styles.workloadMeta}>{leader.scoreText}</Text>}
                  </View>
                  <View style={[styles.progressTrack, styles.leaderTrack]}>
                    {leader.segments.map((segment) =>
                      segment.pct > 0 ? (
                        <View
                          key={segment.key}
                          style={{ width: `${segment.pct}%`, height: 6, backgroundColor: getLeaderSegmentColor(segment.key) }}
                        />
                      ) : null,
                    )}
                  </View>
                  <Text style={[styles.workloadSub, leader.overdue > 0 && styles.workloadMetaLate]}>
                    {[leader.completeText, leader.detail, leader.coverText].filter(Boolean).join(' · ')}
                  </Text>
                </View>
              ))}
              {leaders.length > leaderVisibleCount && (
                <TouchableOpacity onPress={() => setShowAllLeaders((prev) => !prev)}>
                  <Text style={styles.clearSelectionText}>
                    {showAllLeaders ? 'Show fewer leaders' : `Show all ${leaders.length} leaders`}
                  </Text>
                </TouchableOpacity>
              )}
              {!!workload.leaderNote && (
                <View style={styles.noteBox}>
                  <Text style={styles.noteText}>{workload.leaderNote}</Text>
                </View>
              )}
            </>
          )}
        </View>

        {/* Recent People Activity */}
        <View style={styles.card}>
          <View style={styles.cardHeaderRow}>
            <Text style={styles.cardTitle}>Recent People Activity</Text>
            <TouchableOpacity onPress={onOpenHistory} accessibilityLabel="Open All History">
              <MaterialIcons name="open-in-new" size={18} color={AdminColors.textSecondary} />
            </TouchableOpacity>
          </View>
          <View style={styles.recentHead}>
            <Text style={[styles.recentHeadText, styles.recentColName]}>Name</Text>
            <Text style={[styles.recentHeadText, styles.recentColType]}>Type</Text>
            <Text style={[styles.recentHeadText, styles.recentColStatus]}>Status</Text>
            <Text style={[styles.recentHeadText, styles.recentColDate]}>Date</Text>
          </View>
          {isRecentActivityLoading ? (
            <View style={styles.recentLoadingRow}>
              <ActivityIndicator size="small" color={AdminColors.blue700} />
              <Text style={styles.emptyStateText}>Loading recent activity…</Text>
            </View>
          ) : recentActivity.length === 0 ? (
            <View style={styles.recentLoadingRow}>
              <Text style={styles.emptyStateText}>No recent activity recorded yet.</Text>
            </View>
          ) : (
            recentActivity.map((record) => {
              const badge = TYPE_BADGE_COLORS[record.type];
              return (
                <View key={record.id} style={styles.recentRow}>
                  <Text style={[styles.recentName, styles.recentColName]} numberOfLines={1}>{record.employeeName}</Text>
                  <View style={styles.recentColType}>
                    <View style={[styles.typeBadge, { backgroundColor: badge.bg }]}>
                      <Text style={[styles.typeBadgeText, { color: badge.color }]}>{record.type}</Text>
                    </View>
                  </View>
                  <View style={styles.recentColStatus}>
                    <View
                      style={[
                        styles.statusBadge,
                        { backgroundColor: record.isCompleted ? AdminColors.green75 : AdminColors.blue50 },
                      ]}
                    >
                      <Text
                        style={[styles.statusBadgeText, { color: record.isCompleted ? AdminColors.green600 : AdminColors.blue700 }]}
                      >
                        {record.statusLabel}
                      </Text>
                    </View>
                  </View>
                  <Text style={[styles.recentDate, styles.recentColDate]}>{record.dateLabel}</Text>
                </View>
              );
            })
          )}
        </View>
      </View>

      <SetCoverageModal
        visible={isCoverageModalOpen}
        onClose={() => setIsCoverageModalOpen(false)}
        onSubmit={handleCoverageSubmit}
        queueItems={queueItems}
        leaders={leaderOptions}
        isSubmitting={isCoverageSubmitting}
      />

      <ReassignTaskModal
        visible={reassignModalItem !== null}
        onClose={() => setReassignModalItem(null)}
        onSubmit={handleReassignSubmit}
        item={reassignModalItem}
        leaders={leaderOptions}
        userRoleName={userRoleName}
        userRoleId={userRoleId}
        currentUserEmail={userEmail}
        isSubmitting={isReassignSubmitting}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { padding: 20, gap: 16 },
  containerRow: { flexDirection: 'row', alignItems: 'flex-start' },
  containerColumn: { flexDirection: 'column' },

  queueCard: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    padding: 18,
    gap: 12,
  },
  queueCardDesktop: { flex: 2, minWidth: 0 },
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    padding: 18,
    gap: 10,
  },
  sideColumn: { gap: 16 },
  sideColumnDesktop: { flex: 1, minWidth: 300, maxWidth: 400 },
  cardHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontFamily: FontFamily.bold, fontSize: 16, color: AdminColors.textPrimary },
  cardCopy: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textSecondary, lineHeight: 18 },
  emptyState: { padding: 24, alignItems: 'center', gap: 8 },
  emptyStateText: { fontFamily: FontFamily.regular, color: AdminColors.textMuted, fontSize: 13 },

  badgePillBlue: { backgroundColor: AdminColors.blue50, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 },
  badgePillBlueText: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.blue700 },
  badgePillRed: { backgroundColor: AdminColors.red75, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 },
  badgePillRedText: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.red500 },

  // Toolbar
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  scopeGroup: { flexDirection: 'row', borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6, overflow: 'hidden' },
  scopeButton: { paddingHorizontal: 12, paddingVertical: 6, backgroundColor: AdminColors.bgSurface },
  scopeButtonActive: { backgroundColor: AdminColors.blue700 },
  scopeLabel: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.textSecondary },
  scopeLabelActive: { color: AdminColors.textOnDark },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6,
    paddingHorizontal: 10, height: 36, flexGrow: 1, minWidth: 200,
  },
  searchBoxFocused: { borderColor: AdminColors.blue700, borderWidth: 1.5 },
  searchInput: {
    flex: 1,
    height: '100%',
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textPrimary,
    padding: 0,
    outlineWidth: 0,
    outline: 'none',
    boxShadow: 'none',
  } as any,
  countLabel: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textMuted },
  coverageButton: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderColor: AdminColors.blue700, borderRadius: 16,
    paddingHorizontal: 12, paddingVertical: 6,
  },
  coverageButtonText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },

  // Coverage banner
  coverBanner: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    backgroundColor: '#FFF7ED',
    borderWidth: 1,
    borderColor: '#FED7AA',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginTop: 4,
    marginBottom: 4,
  },
  coverBannerTextWrap: {
    flex: 1,
    minWidth: 240,
  },
  coverBannerText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textPrimary,
  },
  coverBannerStrong: {
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
  },
  coverBannerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  coverOfferBtn: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  coverOfferBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
    color: AdminColors.textPrimary,
  },

  // Table
  tableHead: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    borderBottomWidth: 1, borderBottomColor: AdminColors.borderDefault, paddingBottom: 8,
  },
  tableHeadFilterOpen: { zIndex: 40 },
  headCellText: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.textMuted, textTransform: 'uppercase' },
  sortHeader: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  colEmployee: { flex: 2.2 },
  colType: { flex: 1.3, flexDirection: 'row', alignItems: 'center', gap: 4 },
  colAssigned: { flex: 1.4, flexDirection: 'row', alignItems: 'flex-start', gap: 4 },
  colTopic: { flex: 1.8, flexDirection: 'row', alignItems: 'flex-start', gap: 4 },
  colDue: { flex: 1, alignItems: 'center' },
  colFilterOpen: { zIndex: 40 },

  filterBackdrop: {
    position: 'absolute', top: -2000, left: -2000, right: -2000, bottom: -2000, zIndex: 39,
  },
  filterMenu: {
    position: 'absolute', top: 22, left: 0, zIndex: 40,
    backgroundColor: AdminColors.bgSurface, borderWidth: 1, borderColor: AdminColors.borderDefault,
    borderRadius: 8, width: 240, paddingVertical: 10, paddingHorizontal: 10, gap: 8,
    shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.15, shadowRadius: 16,
    elevation: 8,
  },
  filterSearchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6,
    paddingHorizontal: 8, height: 30,
  },
  filterSearchInput: {
    flex: 1, fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textPrimary,
    padding: 0, outlineWidth: 0, outline: 'none',
  } as any,
  filterActionsRow: { flexDirection: 'row', gap: 14 },
  filterActionText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.blue700 },
  filterOptionList: { maxHeight: 180 },
  filterOption: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5 },
  filterOptionText: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textPrimary, flexShrink: 1 },
  filterFooterText: {
    fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted,
    borderTopWidth: 1, borderTopColor: AdminColors.borderDefault, paddingTop: 6,
  },

  // Bands
  bandHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingVertical: 8, paddingHorizontal: 6, borderRadius: 4, marginTop: 8,
  },
  bandHeader_progress: { backgroundColor: AdminColors.blue25 },
  bandHeader_overdue: { backgroundColor: AdminColors.red75 },
  bandHeader_upcoming: { backgroundColor: AdminColors.gray25 },
  bandTitle: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.textPrimary },
  bandCount: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textMuted },
  bandCopy: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted, flex: 1, fontStyle: 'italic' },

  row: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 10, paddingHorizontal: 6,
    borderBottomWidth: 1, borderBottomColor: AdminColors.gray25,
  },
  rowHovered: { backgroundColor: AdminColors.blue25 },
  employeeName: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.blue700 },
  employeeRole: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textSecondary },
  typeBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  typeBadgeText: { fontFamily: FontFamily.medium, fontSize: 11 },
  assignedTo: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textPrimary },
  reassignLink: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.blue700, marginTop: 2 },
  reassignedFromText: {
    fontFamily: FontFamily.regular,
    fontSize: 10,
    color: AdminColors.textMuted,
    marginTop: 2,
  },
  offeredTag: {
    backgroundColor: AdminColors.blue50,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginTop: 2,
    alignSelf: 'flex-start',
  },
  offeredTagText: {
    fontFamily: FontFamily.medium,
    fontSize: 10,
    color: AdminColors.blue700,
  },
  topicTagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  topicTag: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  topicTagText: { fontFamily: FontFamily.medium, fontSize: 10 },
  noOpenAction: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted, fontStyle: 'italic' },
  dueLabel: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textPrimary, textAlign: 'left' },

  // New Hire Onboarding
  onboardingCard: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    backgroundColor: AdminColors.bgSurface,
    padding: 12,
    gap: 10,
  },
  onboardingCardSelected: {
    borderWidth: 1.5,
    borderColor: AdminColors.blue700,
  },
  onboardingTopRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  onboardingAvatar: {
    width: 32, height: 32, borderRadius: 6, backgroundColor: AdminColors.blue50,
    alignItems: 'center', justifyContent: 'center',
  },
  onboardingInfo: { flex: 1 },
  onboardingName: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.blue700 },
  onboardingMeta: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textSecondary },
  onboardingBadge: { borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 },
  onboardingBadgeText: { fontFamily: FontFamily.medium, fontSize: 11 },
  onboardingStripRow: { flexDirection: 'row', gap: 6 },
  onboardingStep: {
    flex: 1, borderRadius: 6, paddingVertical: 6, alignItems: 'center', gap: 3,
    borderWidth: 1, borderColor: 'transparent',
  },
  onboardingStepLabel: { fontFamily: FontFamily.medium, fontSize: 9, color: AdminColors.textMuted },
  onboardingStepDot: { width: 8, height: 8, borderRadius: 4 },
  onboardingStepCount: { fontFamily: FontFamily.regular, fontSize: 10, color: AdminColors.textSecondary },
  clearSelectionBanner: { marginTop: 4, paddingVertical: 8, alignItems: 'center' },
  clearSelectionText: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.blue700, textAlign: 'center' },

  // Location Workload
  toggleGroup: { flexDirection: 'row', borderWidth: 1, borderColor: AdminColors.borderDefault, borderRadius: 6, overflow: 'hidden', alignSelf: 'flex-start' },
  toggleButton: { paddingHorizontal: 12, paddingVertical: 6 },
  toggleButtonActive: { backgroundColor: AdminColors.blue700 },
  toggleLabel: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.textSecondary },
  toggleLabelActive: { color: AdminColors.textOnDark },
  timeframeRow: { flexDirection: 'row', gap: 6 },
  timeframeButton: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: AdminColors.bgPage },
  timeframeButtonActive: { backgroundColor: AdminColors.blue50 },
  timeframeLabel: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.textSecondary },
  timeframeLabelActive: { color: AdminColors.blue700 },

  workloadRow: { gap: 4, marginTop: 4 },
  workloadLabelRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  workloadLabelWithDot: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  workloadDot: { width: 8, height: 8, borderRadius: 4 },
  workloadLabel: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.textPrimary },
  workloadMeta: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted },
  workloadMetaLate: { color: AdminColors.textError },
  progressTrack: { height: 6, borderRadius: 3, backgroundColor: AdminColors.gray50, overflow: 'hidden' },
  progressFill: { height: 6 },
  leaderTrack: { flexDirection: 'row' },
  leaderRowCurrent: { backgroundColor: AdminColors.blue25, borderRadius: 6, padding: 6 },
  workloadSub: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted },
  noteBox: { backgroundColor: AdminColors.gray25, borderRadius: 6, padding: 10, marginTop: 4 },
  noteText: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textSecondary, lineHeight: 16 },

  // Recent People Activity
  recentLoadingRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 16 },
  recentHead: { flexDirection: 'row', gap: 6, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: AdminColors.borderDefault },
  recentHeadText: { fontFamily: FontFamily.medium, fontSize: 10, color: AdminColors.textMuted, textTransform: 'uppercase' },
  recentRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: AdminColors.gray25 },
  recentColName: { flex: 1.6 },
  recentColType: { flex: 1.3 },
  recentColStatus: { flex: 1.3 },
  recentColDate: { flex: 0.9, textAlign: 'right' },
  recentName: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.textPrimary },
  recentDate: { fontFamily: FontFamily.regular, fontSize: 11, color: AdminColors.textMuted },
  statusBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  statusBadgeText: { fontFamily: FontFamily.medium, fontSize: 11 },
});

export default PeopleHubHome;
